import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { formatDate } from '@euler/utils/formatters';
import { memo } from 'react';
import { StyleProp, Text, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';

export const MaintenancePlanInfoView = memo(
  ({
    nextServiceDate,
    nextServiceMileage,
    estimatedNextServiceMileage,
    remark,
    style,
    variant = 'default',
    label,
    onEdit,
  }: {
    nextServiceDate: string | Date | undefined;
    nextServiceMileage: number | undefined;
    estimatedNextServiceMileage: number | undefined;
    remark?: string;
    style?: StyleProp<ViewStyle>;
    variant?: 'default' | 'insurance';
    label?: string;
    onEdit?: () => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    if (variant === 'insurance') {
      return (
        <StackLayout style={style} alignItems="stretch">
          <StackLayout direction="row" alignItems="center" gap={rpx(4)}>
            {nextServiceDate != null ? (
              <>
                <Typography.Text size={rpx(12)} color="#555">
                  {formatDate(nextServiceDate)}
                </Typography.Text>
                <Center.Touchable onPress={onEdit} ml={rpx(2)}>
                  <Icon name="note" color={theme.link} />
                </Center.Touchable>
              </>
            ) : (
              <LinkButton
                text="设置到期日期"
                textStyle={{ fontSize: rpx(12) }}
                onPress={onEdit}
                icon={
                  <Icon
                    name="plus"
                    color={theme.link}
                    css={`
                      margin-right: ${rpx(-4)}px;
                      margin-left: ${rpx(-2)}px;
                    `}
                  />
                }
              />
            )}
          </StackLayout>
          {remark?.trim() ? (
            <Typography.Text size={rpx(12)} color="#555">
              提醒备注: {remark}
            </Typography.Text>
          ) : null}
        </StackLayout>
      );
    }

    return (
      <StackLayout style={style} alignItems="stretch">
        <StackLayout direction="row" alignItems="center" gap={rpx(4)}>
          <Typography.Text size={rpx(12)} color="#555" numberOfLines={1}>
            {label ? `${label}:` : '保养计划:'}
          </Typography.Text>
          {nextServiceDate == null ? (
            <LinkButton
              text={label ? `设置${label}` : '设置保养计划'}
              textStyle={{ fontSize: rpx(12) }}
              onPress={onEdit}
              icon={
                <Icon
                  name="plus"
                  color={theme.link}
                  css={`
                    margin-right: ${rpx(-4)}px;
                    margin-left: ${rpx(-2)}px;
                  `}
                />
              }
            />
          ) : (
            <>
              <Typography.Text size={rpx(12)} color="#555">
                {` ${formatDate(nextServiceDate)} (${
                  nextServiceMileage == null ? '~' : ''
                }${
                  nextServiceMileage ?? estimatedNextServiceMileage ?? '未知'
                }公里)`}
              </Typography.Text>
              <Center.Touchable onPress={onEdit} ml={rpx(2)}>
                <Icon name="note" color={theme.link} />
              </Center.Touchable>
            </>
          )}
        </StackLayout>
        {remark?.trim() ? (
          <Typography.Text size={rpx(12)} color="#555">
            计划备注: {remark}
          </Typography.Text>
        ) : null}
      </StackLayout>
    );
  },
);

export const CustomerSignInfo = memo(
  ({
    imgUrl,
    unavailableReason,
    unavailableRemark,
  }: {
    imgUrl?: string;
    unavailableReason?: string;
    unavailableRemark?: string;
  }) => {
    const { rpx } = useResponsiveContext();
    if (imgUrl) {
      return (
        <StackLayout
          direction="row"
          alignItems="center"
          gap={rpx(6)}
          style={{ marginTop: rpx(2) }}
        >
          <Typography.Text size={rpx(12)} color="#555">
            客户签字:
          </Typography.Text>
          <Img
            uri={imgUrl}
            width={rpx(60)}
            height={rpx(24)}
            resizeMode="contain"
          />
          <Icon
            name="checkmark-circle"
            size={rpx(12)}
            color={StatusColors.Success}
          />
        </StackLayout>
      );
    }
    if (unavailableReason) {
      return (
        <StackLayout gap={rpx(2)} style={{ marginTop: rpx(2) }}>
          <Typography.Text size={rpx(12)} color="#555">
            {'客户签字: '}
            <Text style={{ color: StatusColors.Warn }}>
              不可用 ({unavailableReason})
            </Text>
          </Typography.Text>
          {unavailableRemark?.trim() ? (
            <Typography.Text size={rpx(12)} color="#555">
              {'签字备注: '}
              <Text style={{ color: '#777' }}>{unavailableRemark}</Text>
            </Typography.Text>
          ) : null}
        </StackLayout>
      );
    }
    return (
      <Typography.Text
        size={rpx(12)}
        color="#555"
        style={{ marginTop: rpx(2) }}
      >
        {'客户签字: '}
        <Text style={{ color: StatusColors.Warn }}>未签字</Text>
      </Typography.Text>
    );
  },
);
